import React, { useState, useEffect } from "react";
import { useRef } from "react";
import axios from "axios";
import { useParams, useSearchParams } from "react-router-dom";

const ApplicationFormPage = () => {
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get("campaign_id") || "";
  const [formData, setFormData] = useState({
    campaign_id: campaignId,
    full_name: "",
    phone_number: "",
    email: "",
    resume: null,
    total_experience_years: "",
    current_location: "",
    highest_education: "",
    current_company_name: "",
    current_designation: "",
    current_ctc_month: "",
    expected_ctc_month: "",
    notice_period: "",
    position_applied_for: "",
    source_of_application: "",
    reason_for_exploring: "",
    current_job_role: "",
    comfortable_travel: false,
    comfortable_six_days: false,
    short_term_goal: "",
    long_term_goal: "",
    swot: "",
    ideal_candidate: "",
  });

  const resumeInputRef = useRef(null);

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "comfortable_travel" || name === "comfortable_six_days"
          ? value === "1"
          : type === "checkbox"
            ? checked
            : type === "file"
              ? files[0]
              : value,
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    // if (!formData.campaign_id) errors.campaign_id = 'Campaign ID is required';
    if (!formData.full_name.trim()) errors.full_name = "Full name is required";
    if (!formData.phone_number.trim())
      errors.phone_number = "Phone number is required";
    if (!formData.email.trim()) errors.email = "Email is required";
    if (!formData.total_experience_years)
      errors.total_experience_years = "Total experience is required";
    if (!formData.current_location.trim())
      errors.current_location = "Current location is required";
    if (!formData.highest_education.trim())
      errors.highest_education = "Highest education is required";
    if (!formData.current_company_name.trim())
      errors.current_company_name = "Current company name is required";
    if (!formData.current_designation.trim())
      errors.current_designation = "Current designation is required";
    if (!formData.current_ctc_month)
      errors.current_ctc_month = "Current CTC is required";
    if (!formData.expected_ctc_month)
      errors.expected_ctc_month = "Expected CTC is required";
    if (!formData.notice_period.trim())
      errors.notice_period = "Notice period is required";
    if (!formData.position_applied_for.trim())
      errors.position_applied_for = "Position applied for is required";
    if (!formData.source_of_application.trim())
      errors.source_of_application = "Source of application is required";
    if (!formData.reason_for_exploring.trim())
      errors.reason_for_exploring = "Reason for exploring is required";
    if (!formData.current_job_role.trim())
      errors.current_job_role = "Current job role is required";
    if (!formData.short_term_goal.trim())
      errors.short_term_goal = "Short term goal is required";
    if (!formData.long_term_goal.trim())
      errors.long_term_goal = "Long term goal is required";
    if (!formData.swot.trim()) errors.swot = "SWOT analysis is required";
    if (!formData.ideal_candidate.trim())
      errors.ideal_candidate = "Ideal candidate description is required";
    // Resume validation
    if (!formData.resume) {
      errors.resume = "Resume file is required";
    } else {
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!allowedTypes.includes(formData.resume.type)) {
        errors.resume = "Only PDF, DOC, or DOCX files are allowed";
      }
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    // Phone number validation (10 digits)
    const phoneRegex = /^[0-9]{10}$/;
    if (formData.phone_number && !phoneRegex.test(formData.phone_number)) {
      errors.phone_number = "Phone number must be exactly 10 digits";
    }

    // Number validations
    if (
      formData.total_experience_years &&
      formData.total_experience_years < 0
    ) {
      errors.total_experience_years = "Experience cannot be negative";
    }
    if (formData.current_ctc_month && formData.current_ctc_month < 0) {
      errors.current_ctc_month = "Current CTC cannot be negative";
    }
    if (formData.expected_ctc_month && formData.expected_ctc_month < 0) {
      errors.expected_ctc_month = "Expected CTC cannot be negative";
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare form data for file upload
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === "resume" && value) {
          data.append("resume", value);
        } else if (
          key === "comfortable_travel" ||
          key === "comfortable_six_days"
        ) {
          data.append(key, value ? "1" : "0");
        } else {
          data.append(key, value);
        }
      });
      const response = await axios.post(
        "https://hrmsnew.prepstripe.com/api/applications",
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      // console.log('Application submitted successfully:', response.data);
      setShowSuccessModal(true);
      // Reset form after successful submission
      setFormData({
        // campaign_id: '',
        full_name: "",
        phone_number: "",
        email: "",
        resume: null,
        total_experience_years: "",
        current_location: "",
        highest_education: "",
        current_company_name: "",
        current_designation: "",
        current_ctc_month: "",
        expected_ctc_month: "",
        notice_period: "",
        position_applied_for: "",
        source_of_application: "",
        reason_for_exploring: "",
        current_job_role: "",
        comfortable_travel: false,
        comfortable_six_days: false,
        short_term_goal: "",
        long_term_goal: "",
        swot: "",
        ideal_candidate: "",
      });
      if (resumeInputRef.current) resumeInputRef.current.value = "";
      setFormErrors({});
    } catch (error) {
      console.error("Error submitting application:", error);
      setErrorMessage(
        error.response?.data?.message ||
          "Failed to submit application. Please try again.",
      );
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    setFormData({
      //   campaign_id: '',
      full_name: "",
      phone_number: "",
      email: "",
      resume: null,
      total_experience_years: "",
      current_location: "",
      highest_education: "",
      current_company_name: "",
      current_designation: "",
      current_ctc_month: "",
      expected_ctc_month: "",
      notice_period: "",
      position_applied_for: "",
      source_of_application: "",
      reason_for_exploring: "",
      current_job_role: "",
      comfortable_travel: false,
      comfortable_six_days: false,
      short_term_goal: "",
      long_term_goal: "",
      swot: "",
      ideal_candidate: "",
    });
    if (resumeInputRef.current) resumeInputRef.current.value = "";
    setFormErrors({});
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden">
          {/* Header */}
          <div className="bg-blue-600 dark:bg-blue-700 px-6 py-4">
            <h1 className="text-2xl font-bold text-white">
              Job Application Form
            </h1>
            <p className="text-blue-100 mt-1">
              Please fill in all the required information
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    formErrors.full_name ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter your full name"
                />
                {formErrors.full_name && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.full_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    formErrors.phone_number
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter 10-digit phone number"
                />
                {formErrors.phone_number && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.phone_number}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    formErrors.email ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter your email address"
                />
                {formErrors.email && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.email}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Total Experience (Years){" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="total_experience_years"
                  value={formData.total_experience_years}
                  onChange={handleChange}
                  min="0"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    formErrors.total_experience_years
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter years of experience"
                />
                {formErrors.total_experience_years && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.total_experience_years}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current Location <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="current_location"
                  value={formData.current_location}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    formErrors.current_location
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="e.g., Pune, India"
                />
                {formErrors.current_location && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.current_location}
                  </p>
                )}
              </div>
            </div>

            {/* Education & Current Job */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Highest Education <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="highest_education"
                  value={formData.highest_education}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    formErrors.highest_education
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="e.g., MBA, B.Tech, etc."
                />
                {formErrors.highest_education && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.highest_education}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="current_company_name"
                  value={formData.current_company_name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    formErrors.current_company_name
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter current company name"
                />
                {formErrors.current_company_name && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.current_company_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current Designation <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="current_designation"
                  value={formData.current_designation}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    formErrors.current_designation
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter current designation"
                />
                {formErrors.current_designation && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.current_designation}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current CTC (Monthly) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="current_ctc_month"
                  value={formData.current_ctc_month}
                  onChange={handleChange}
                  min="0"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    formErrors.current_ctc_month
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter current monthly CTC"
                />
                {formErrors.current_ctc_month && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.current_ctc_month}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Expected CTC (Monthly) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="expected_ctc_month"
                  value={formData.expected_ctc_month}
                  onChange={handleChange}
                  min="0"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    formErrors.expected_ctc_month
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter expected monthly CTC"
                />
                {formErrors.expected_ctc_month && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.expected_ctc_month}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notice Period <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="notice_period"
                  value={formData.notice_period}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    formErrors.notice_period
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="e.g., 2 months"
                />
                {formErrors.notice_period && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.notice_period}
                  </p>
                )}
              </div>
            </div>

            {/* Application Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Position Applied For <span className="text-red-500">*</span>
                </label>
                <select
                  name="position_applied_for"
                  value={formData.position_applied_for}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${formErrors.position_applied_for ? "border-red-500" : "border-gray-300"}`}
                >
                  <option value="">Select position</option>
                  <option value="Sales Development Representative">
                    Sales Development Representative
                  </option>
                  <option value="Red Teamer - Operations">
                    Red Teamer - Operations
                  </option>
                  <option value="Telecalling / Customer Support">
                    Telecalling / Customer Support
                  </option>
                  <option value="Data Entry Operator">
                    Data Entry Operator
                  </option>
                  <option value="MIS Executive">MIS Executive</option>
                  <option value="Quality Analyst Backoffice">
                    Quality Analyst Backoffice
                  </option>
                  <option value="Android Developer">Android Developer</option>
                  <option value="Full Stack Developer - Java Springboot">
                    Full Stack Developer - Java Springboot
                  </option>
                  <option value="Accounts & Finance">Accounts & Finance</option>
                  <option value="Network Support Engineer">
                    Network Support Engineer
                  </option>
                  <option value="Marketing">Marketing</option>
                  <option value="CA">CA</option>
                  <option value="Project Manager">Project Manager</option>
                  <option value="Human Resource">Human Resource</option>
                </select>
                {formErrors.position_applied_for && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.position_applied_for}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Source of Application <span className="text-red-500">*</span>
                </label>
                <select
                  name="source_of_application"
                  value={formData.source_of_application}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    formErrors.source_of_application
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                >
                  <option value="">Select source</option>
                  <option value="LinkedIn">LinkedIn</option>
                  <option value="Indeed">Indeed</option>
                  <option value="Naukri">Naukri</option>
                  <option value="Referral">Referral</option>
                  <option value="Company Website">Company Website</option>
                  <option value="Other">Other</option>
                </select>
                {formErrors.source_of_application && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.source_of_application}
                  </p>
                )}
              </div>
            </div>

            {/* Text Areas */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason for Exploring New Opportunities{" "}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="reason_for_exploring"
                  value={formData.reason_for_exploring}
                  onChange={handleChange}
                  rows="3"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    formErrors.reason_for_exploring
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Explain why you're looking for new opportunities"
                />
                {formErrors.reason_for_exploring && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.reason_for_exploring}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current Job Role Description{" "}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="current_job_role"
                  value={formData.current_job_role}
                  onChange={handleChange}
                  rows="3"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    formErrors.current_job_role
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Describe your current job responsibilities"
                />
                {formErrors.current_job_role && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.current_job_role}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Short Term Goal <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="short_term_goal"
                  value={formData.short_term_goal}
                  onChange={handleChange}
                  rows="2"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    formErrors.short_term_goal
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="What are your short term career goals?"
                />
                {formErrors.short_term_goal && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.short_term_goal}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Long Term Goal <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="long_term_goal"
                  value={formData.long_term_goal}
                  onChange={handleChange}
                  rows="2"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    formErrors.long_term_goal
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="What are your long term career aspirations?"
                />
                {formErrors.long_term_goal && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.long_term_goal}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  SWOT Analysis <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="swot"
                  value={formData.swot}
                  onChange={handleChange}
                  rows="4"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    formErrors.swot ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Strengths: ...; Weaknesses: ...; Opportunities: ...; Threats: ..."
                />
                {formErrors.swot && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.swot}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ideal Candidate Description{" "}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="ideal_candidate"
                  value={formData.ideal_candidate}
                  onChange={handleChange}
                  rows="3"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    formErrors.ideal_candidate
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Describe what makes an ideal candidate for this role"
                />
                {formErrors.ideal_candidate && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.ideal_candidate}
                  </p>
                )}
              </div>
            </div>

            {/* Yes/No Dropdowns for Comfortable Travel and Six Days */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Comfortable with travel requirements
                </label>
                <select
                  name="comfortable_travel"
                  value={formData.comfortable_travel ? "1" : "0"}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                >
                  <option value="0">No</option>
                  <option value="1">Yes</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Comfortable with six-day work week
                </label>
                <select
                  name="comfortable_six_days"
                  value={formData.comfortable_six_days ? "1" : "0"}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                >
                  <option value="0">No</option>
                  <option value="1">Yes</option>
                </select>
              </div>
            </div>

            {/* Resume Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Resume <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                name="resume"
                accept=".pdf,.doc,.docx"
                onChange={handleChange}
                ref={resumeInputRef}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
              />
              {formData.resume && (
                <p className="text-green-600 text-sm mt-1">
                  Selected: {formData.resume.name}
                </p>
              )}
              {formErrors.resume && (
                <p className="text-red-500 text-sm mt-1">{formErrors.resume}</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleClear}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600"
              >
                Clear Form
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Submitting..." : "Submit Application"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-8 w-8 text-green-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Success!
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Your application has been submitted successfully.
                </p>
              </div>
            </div>
            <div className="mt-4">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-8 w-8 text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Error
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {errorMessage}
                </p>
              </div>
            </div>
            <div className="mt-4">
              <button
                onClick={() => setShowErrorModal(false)}
                className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationFormPage;
